import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const Home = () => {
    const [text, setText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [textIndex, setTextIndex] = useState(0);
    const texts = ["Programar", "Ler", "Estudar", "Aprender", "Criar", "Inovar", "Desenvolver", "Resolver Problemas"];
    const navigate = useNavigate();
    const canvasRef = useRef(null);
    const mouse = useRef({ x: null, y: null });
    const particlesArray = useRef([]);

    const typingSpeed = 150;
    const deletingSpeed = 100;
    const delay = 2000;

    // Orb class - esferas flutuantes interativas
    class Orb {
        constructor(canvas) {
            this.x = Math.random() * canvas.width;
            this.y = Math.random() * canvas.height;
            this.radius = Math.random() * 80 + 40;
            this.baseRadius = this.radius;
            this.vx = (Math.random() - 0.5) * 0.5;
            this.vy = (Math.random() - 0.5) * 0.5;
            this.hue = Math.random() * 20; // Variação de tons de vermelho
            this.opacity = Math.random() * 0.3 + 0.15;
            this.pulseSpeed = Math.random() * 0.02 + 0.01;
            this.pulseOffset = Math.random() * Math.PI * 2;
        }

        update(canvas, mouseX, mouseY, time) {
            // Movimento suave
            this.x += this.vx;
            this.y += this.vy;

            // Bounce nas bordas
            if (this.x - this.radius < 0 || this.x + this.radius > canvas.width) {
                this.vx *= -1;
                this.x = Math.max(this.radius, Math.min(canvas.width - this.radius, this.x));
            }
            if (this.y - this.radius < 0 || this.y + this.radius > canvas.height) {
                this.vy *= -1;
                this.y = Math.max(this.radius, Math.min(canvas.height - this.radius, this.y));
            }

            // Pulsação
            this.radius = this.baseRadius + Math.sin(time * this.pulseSpeed + this.pulseOffset) * 15;

            // Atração/repulsão do mouse
            if (mouseX !== null && mouseY !== null) {
                const dx = mouseX - this.x;
                const dy = mouseY - this.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const maxDistance = 250;

                if (distance < maxDistance) {
                    const force = (maxDistance - distance) / maxDistance * 0.5;
                    this.x -= (dx / distance) * force * 3;
                    this.y -= (dy / distance) * force * 3;
                }
            }
        }

        draw(ctx) {
            // Gradiente radial
            const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
            gradient.addColorStop(0, `hsla(${this.hue}, 85%, 55%, ${this.opacity})`);
            gradient.addColorStop(0.5, `hsla(${this.hue}, 85%, 50%, ${this.opacity * 0.6})`);
            gradient.addColorStop(1, `hsla(${this.hue}, 85%, 45%, 0)`);

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    // Canvas animation
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let animationFrameId;
        let time = 0;
        const orbs = [];

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initOrbs();
        };

        const initOrbs = () => {
            orbs.length = 0;
            const orbCount = Math.min(8, Math.floor((canvas.width * canvas.height) / 80000));
            for (let i = 0; i < orbCount; i++) {
                orbs.push(new Orb(canvas));
            }
        };

        const drawConnections = () => {
            for (let i = 0; i < orbs.length; i++) {
                for (let j = i + 1; j < orbs.length; j++) {
                    const dx = orbs[j].x - orbs[i].x;
                    const dy = orbs[j].y - orbs[i].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const maxDistance = 300;

                    if (distance < maxDistance) {
                        const opacity = (1 - distance / maxDistance) * 0.50;
                        const gradient = ctx.createLinearGradient(orbs[i].x, orbs[i].y, orbs[j].x, orbs[j].y);
                        gradient.addColorStop(0, `rgba(220, 38, 38, ${opacity})`);
                        gradient.addColorStop(0.5, `rgba(239, 68, 68, ${opacity * 5})`);
                        gradient.addColorStop(1, `rgba(220, 38, 38, ${opacity})`);

                        ctx.strokeStyle = gradient;
                        ctx.lineWidth = 2;
                        ctx.beginPath();
                        ctx.moveTo(orbs[i].x, orbs[i].y);
                        ctx.lineTo(orbs[j].x, orbs[j].y);
                        ctx.stroke();
                    }
                }
            }
        };

        const animate = () => {
            time += 1;

            // Limpa com gradiente de fundo
            const bgGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            bgGradient.addColorStop(0, 'rgba(249, 250, 251, 0.13)');
            bgGradient.addColorStop(1, 'rgba(255, 255, 255, 0.98)');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Desenha conexões primeiro
            drawConnections();

            // Atualiza e desenha orbs
            orbs.forEach(orb => {
                orb.update(canvas, mouse.current.x, mouse.current.y, time);
                orb.draw(ctx);
            });

            // Efeito de glow no mouse
            if (mouse.current.x !== null && mouse.current.y !== null) {
                const mouseGradient = ctx.createRadialGradient(
                    mouse.current.x, mouse.current.y, 0,
                    mouse.current.x, mouse.current.y, 100
                );
                mouseGradient.addColorStop(0, 'rgba(220, 38, 38, 0.1)');
                mouseGradient.addColorStop(0.5, 'rgba(239, 68, 68, 0.05)');
                mouseGradient.addColorStop(1, 'rgba(248, 113, 113, 0)');

                ctx.fillStyle = mouseGradient;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouse.current.x = e.clientX - rect.left;
            mouse.current.y = e.clientY - rect.top;
        };

        const handleMouseLeave = () => {
            mouse.current.x = null;
            mouse.current.y = null;
        };

        resizeCanvas();
        animate();
        window.addEventListener("resize", resizeCanvas);
        canvas.addEventListener("mousemove", handleMouseMove);
        canvas.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener("resize", resizeCanvas);
            canvas.removeEventListener("mousemove", handleMouseMove);
            canvas.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, []);

    // Typing effect logic
    useEffect(() => {
        let interval;
        let timeout;

        if (!isDeleting) {
            interval = setInterval(() => {
                setText((prev) => prev + texts[textIndex].charAt(prev.length));
                if (text.length === texts[textIndex].length) {
                    clearInterval(interval);
                    timeout = setTimeout(() => setIsDeleting(true), delay);
                }
            }, typingSpeed);
        } else {
            interval = setInterval(() => {
                setText((prev) => prev.slice(0, prev.length - 1));
                if (text.length === 0) {
                    clearInterval(interval);
                    setIsDeleting(false);
                    setTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
                }
            }, deletingSpeed);
        }

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [text, isDeleting, textIndex, texts]);

    return (
        <div className="bg-white text-gray-900 font-sans">
            {/* Hero Section com Partículas */}
            <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
                <canvas
                    ref={canvasRef}
                    className="absolute inset-x-0 top-0 w-full h-screen z-0 pointer-events-auto"
                />
                <section className="container mx-auto px-6 py-24 flex flex-col-reverse md:flex-row items-center justify-between relative z-10 pointer-events-none min-h-[85vh]">
                    <div className="mt-10 md:mt-0 flex flex-col items-center md:items-start md:w-1/2 mb-10 md:mb-0 pointer-events-auto">
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-center md:text-left leading-tight">
                            <span className="text-gray-900">Olá, eu sou </span>
                            <span className="gradient-text text-red-600">Eduardo da Silva Fontes</span>
                        </h1>
                        
                        <div className="text-2xl sm:text-3xl md:text-4xl mb-8 text-center md:text-left">
                            <span className="text-gray-700">Eu gosto de </span>
                            <span className="text-red-600 font-semibold border-r-2 border-red-600 animate-pulse">
                                {text}
                            </span>
                        </div>
                        
                        <p className="text-base sm:text-lg md:text-xl text-gray-800 mb-10 text-center md:text-left max-w-lg leading-relaxed">
                            Desenvolvedor apaixonado por criar soluções elegantes e funcionais. 
                            Transformo ideias em código limpo e experiências digitais memoráveis.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 pointer-events-auto">
                            <button 
                                onClick={() => navigate('/projetos')}
                                className="px-8 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 transform"
                            >
                                Ver Projetos
                            </button>
                            <button 
                                onClick={() => navigate('/sobre-mim')}
                                className="px-8 py-3 border-2 border-red-600 text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 transform"
                            >
                                Sobre Mim
                            </button>
                        </div>
                    </div>

                    <div className="relative pointer-events-auto">
                        <div className="absolute -inset-4 bg-gradient-to-r from-red-400 to-red-600 rounded-full blur-xl opacity-30"></div>
                        <img
                            src="Eduardo.jpeg"
                            alt="Edu Fontes"
                            className="relative rounded-full object-cover w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 border-4 border-white shadow-2xl z-10 hover:scale-105 transition-transform duration-500"
                        />
                    </div>
                </section>
            </div>

            {/* Tech Stack Section */}
            <section className="py-16 relative z-10 bg-white">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-12">
                        <span className="gradient-text">
                            Minhas Habilidades
                        </span>
                    </h2>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                        {/* Linguagens */}
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg"
                                    alt="Python"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Python</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg"
                                    alt="Java"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Java</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg"
                                    alt="JavaScript"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">JavaScript</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg"
                                    alt="TypeScript"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">TypeScript</span>
                        </div>

                        {/* Frontend */}
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original-wordmark.svg"
                                    alt="React"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">React</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original-wordmark.svg"
                                    alt="HTML"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">HTML5</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original-wordmark.svg"
                                    alt="CSS"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">CSS3</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg"
                                    alt="Figma"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Figma</span>
                        </div>

                        {/* Backend/Databases */}
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flask/flask-original.svg"
                                    alt="Flask"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Flask</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original-wordmark.svg"
                                    alt="MongoDB"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">MongoDB</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original-wordmark.svg"
                                    alt="MySQL"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">MySQL</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original-wordmark.svg"
                                    alt="Redis"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Redis</span>
                        </div>
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cassandra/cassandra-original-wordmark.svg"
                                    alt="Redis"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Cassandra</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg"
                                    alt="Redis"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">NextJs</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/spring/spring-original.svg"
                                    alt="Redis"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Spring</span>
                        </div>

                        {/* Cloud/DevOps */}
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg"
                                    alt="AWS"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">AWS</span>
                        </div>
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original-wordmark.svg"
                                    alt="android"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">PostgreSQL</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original-wordmark.svg"
                                    alt="Redis"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Redis</span>
                        </div>
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/prisma/prisma-original-wordmark.svg"
                                    alt="android"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Prisma</span>
                        </div>
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/neovim/neovim-original-wordmark.svg"
                                    alt="android"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">NeoVim</span>
                        </div>
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original-wordmark.svg"
                                    alt="android"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Vue.js</span>
                        </div>
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original-wordmark.svg"
                                    alt="Docker"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Docker</span>
                        </div>

                        {/* Ferramentas */}
                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg"
                                    alt="GitHub"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">GitHub</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/canva/canva-original.svg"
                                    alt="Canva"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Canva</span>
                        </div>

                        <div className="flex flex-col items-center group">
                            <div className="p-5 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-red-500 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <img
                                    src="https://ollama.com/public/ollama.png"
                                    alt="Ollama"
                                    className="w-14 h-14 object-contain group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className="mt-3 text-sm font-semibold text-gray-700">Ollama</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;